<template>
  <f7-page>
    <f7-navbar :title="$t('tontine.contributionTitle')" back-link />
    <AppLayout>
      <template #main>
        <f7-block v-if="loading" strong inset>
          <f7-preloader /> {{ $t("tontine.loading") }}
        </f7-block>

        <template v-else-if="contribution">
          <!-- Contribution card -->
          <f7-block strong inset>
            <div class="contribution-head">
              <div class="avatar">{{ initials }}</div>
              <div class="contribution-head-body">
                <h2>{{ memberName }}</h2>
                <f7-badge :class="statusClass">{{ statusLabel }}</f7-badge>
              </div>
            </div>
            <div class="amount">{{ money(contribution.expectedAmount) }}</div>
            <p
              v-if="
                contribution.expectedAmount != null &&
                contribution.confirmedAmount != null &&
                Number(contribution.confirmedAmount) !==
                  Number(contribution.expectedAmount)
              "
              class="text-color-gray"
            >
              {{ $t("tontine.confirmedAmount") }} :
              {{ money(contribution.confirmedAmount) }}
            </p>
            <f7-list no-hairlines>
              <f7-list-item
                :title="$t('tontine.dueDate')"
                :after="formatDate(contribution.dueAt)"
              />
              <f7-list-item
                v-if="contribution.paidAt"
                :title="$t('tontine.paidDate')"
                :after="formatDate(contribution.paidAt)"
              />
              <f7-list-item
                v-if="contribution.confirmedBy"
                :title="$t('tontine.confirmedBy')"
                :after="contribution.confirmedBy"
              />
              <f7-list-item
                v-if="contribution.confirmationSource"
                :title="$t('tontine.confirmationSource')"
                :after="confirmationSourceLabel"
              />
              <f7-list-item
                v-if="contribution.note"
                :title="$t('tontine.note')"
                :after="contribution.note"
              />
            </f7-list>
            <a
              v-if="contribution.receiptUrl"
              :href="contribution.receiptUrl"
              target="_blank"
              class="receipt-link"
            >
              <f7-icon f7="doc_text_fill" size="20" />
              {{ $t("tontine.viewReceipt") }}
            </a>
          </f7-block>

          <!-- Member actions -->
          <f7-block v-if="canSubmitReceipt" strong inset>
            <f7-button fill large @click="openReceiptPopup">{{
              $t("tontine.addReceipt")
            }}</f7-button>
            <p class="hint">{{ $t("tontine.addReceiptHint") }}</p>
          </f7-block>
          <f7-block v-if="canPayOnline" strong inset>
            <f7-button fill large @click="payNow">{{
              $t("tontine.payOnline")
            }}</f7-button>
            <p class="hint">{{ $t("tontine.payOnlineHint") }}</p>
          </f7-block>
          <f7-block v-if="canConfirm" strong inset>
            <f7-button fill large @click="openConfirmDialog">{{
              $t("tontine.confirmContribution")
            }}</f7-button>
            <p class="hint">{{ $t("tontine.confirmContributionHint") }}</p>
          </f7-block>

          <!-- Next steps / pedagogical -->
          <f7-block strong inset class="info-notice">
            <p class="text-color-gray">
              <strong>{{ $t("tontine.whatHappensNext") }}</strong
              ><br />
              {{ whatHappensNextText }}
            </p>
          </f7-block>
        </template>
      </template>
    </AppLayout>
    <!-- Receipt upload popup -->
    <AdaptiveSheet
      v-model:opened="receiptPopupOpen"
      close-by-outside-click
      close-on-escape
    >
      <f7-page>
        <f7-navbar :title="$t('tontine.addReceipt')">
          <f7-nav-right
            ><f7-link @click="receiptPopupOpen = false">{{
              $t("common.close")
            }}</f7-link></f7-nav-right
          >
        </f7-navbar>
        <f7-block strong inset>
          <div class="upload-zone" @click="receiptInput?.click()">
            <f7-icon
              v-if="!receiptFile"
              f7="photo_badge_plus"
              size="44"
              color="gray"
            />
            <img v-else :src="receiptPreview" class="receipt-preview" />
            <p v-if="!receiptFile" class="text-color-gray">
              {{ $t("tontine.tapToAddReceipt") }}
            </p>
            <p v-else class="receipt-name">{{ receiptFile.name }}</p>
          </div>
          <input
            ref="receiptInput"
            type="file"
            accept="image/*"
            class="hidden-input"
            @change="onReceiptPicked"
          />
          <f7-list>
            <f7-list-input
              :label="$t('tontine.note')"
              type="textarea"
              :placeholder="$t('tontine.receiptNotePlaceholder')"
              v-model:value="receiptNote"
            />
          </f7-list>
          <f7-button fill large :disabled="uploading" @click="submitReceipt">
            <f7-preloader v-if="uploading" size="20" color="white" />
            <span v-else>{{ $t("tontine.submitReceipt") }}</span>
          </f7-button>
        </f7-block>
      </f7-page>
    </AdaptiveSheet>
  </f7-page>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { f7 } from "framework7-vue";
import { useI18n } from "vue-i18n";
import { useTontineStore } from "@/js/pinia-stores/tontine";
import { useKeycloakStore } from "@/js/pinia-stores/keycloak";
import usePaymentTrigger from "@/js/composables/usePaymentTrigger";
import { uploadImageToR2 } from "@/js/composables/useR2Image";
import AdaptiveSheet from "../../components/common/AdaptiveSheet.vue";
import AppLayout from "../../components/common/AppLayout.vue";

const { t } = useI18n();
const props = defineProps({
  campaignId: String,
  groupId: String,
  contributionId: String,
  f7route: Object,
});
const store = useTontineStore();
const keycloak = useKeycloakStore();
const payment = usePaymentTrigger();

const plan = computed(() => store.plan);
const cycleDetail = computed(() => store.cycleDetail);
const loading = computed(() => store.loading);
const subject = computed(() => keycloak.subject);

const receiptPopupOpen = ref(false);
const receiptFile = ref(null);
const receiptPreview = ref("");
const receiptNote = ref("");
const uploading = ref(false);
const receiptInput = ref(null);

const contribution = computed(() => {
  const list = cycleDetail.value?.contributions || [];
  return list.find((c) => c.id === props.contributionId);
});

const memberName = computed(
  () => contribution.value?.memberDisplayName || t("tontine.member"),
);
const initials = computed(() => {
  const name = memberName.value.trim();
  return name
    ? name
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p[0])
        .join("")
        .toUpperCase()
    : "?";
});
const isMine = computed(
  () => contribution.value?.memberUserId === subject.value,
);
const canSubmitReceipt = computed(
  () =>
    isMine.value &&
    plan.value?.operatingMode === "COORDINATION_ONLY" &&
    ["DUE", "PAYMENT_REQUESTED", "OVERDUE"].includes(
      contribution.value?.status,
    ),
);
const canPayOnline = computed(
  () =>
    isMine.value &&
    plan.value?.operatingMode !== "COORDINATION_ONLY" &&
    ["DUE", "PAYMENT_REQUESTED", "OVERDUE"].includes(
      contribution.value?.status,
    ),
);
const canConfirm = computed(
  () =>
    plan.value?.canManage &&
    [
      "DUE",
      "RECEIPT_SUBMITTED",
      "OVERDUE",
      "PAYMENT_REQUESTED",
      "PAYMENT_PENDING_CONFIRMATION",
    ].includes(contribution.value?.status),
);

const statusLabel = computed(() =>
  contributionStatusLabel(contribution.value?.status),
);
const statusClass = computed(() =>
  contributionStatusClass(contribution.value?.status),
);
const confirmationSourceLabel = computed(
  () =>
    ({
      PSP_WEBHOOK: t("tontine.confirmationSource.PSP_WEBHOOK"),
      TREASURER_CONFIRMED: t("tontine.confirmationSource.TREASURER_CONFIRMED"),
      MEMBER_RECEIPT: t("tontine.confirmationSource.MEMBER_RECEIPT"),
      IMPORTED_HISTORY: t("tontine.confirmationSource.IMPORTED_HISTORY"),
      ADMIN_ADJUSTMENT: t("tontine.confirmationSource.ADMIN_ADJUSTMENT"),
    }[contribution.value?.confirmationSource] ||
    contribution.value?.confirmationSource),
);

const whatHappensNextText = computed(() => {
  const s = contribution.value?.status;
  if (s === "RECEIPT_SUBMITTED" || s === "PAYMENT_PENDING_CONFIRMATION")
    return t("tontine.whatNext.receiptSubmitted");
  if (s === "CONFIRMED") return t("tontine.whatNext.confirmed");
  if (s === "OVERDUE") return t("tontine.whatNext.overdue");
  if (s === "EXCUSED" || s === "WAIVED") return t("tontine.whatNext.excused");
  return t("tontine.whatNext.toPay");
});

onMounted(load);

async function load() {
  try {
    const cycleId =
      props.f7route?.query?.cycleId || plan.value?.currentCycle?.id;
    await Promise.all([
      store.loadSummary(props.campaignId),
      cycleId
        ? store.fetchCycleDetail(props.campaignId, cycleId)
        : Promise.resolve(),
    ]);
  } catch (err) {
    f7.dialog.alert(
      err.response?.data?.message || err.message || t("tontine.loadError"),
    );
  }
}

function payNow() {
  payment.triggerCampaignContribution({
    campaignId: props.campaignId,
    groupId: props.groupId,
    amount: contribution.value?.expectedAmount,
    campaignType: "TONTINE",
    currency: plan.value?.currency,
    campaignName: plan.value?.campaignName,
    description: t("tontine.contributionForCycle", {
      number: cycleDetail.value?.cycle?.cycleNumber || 1,
    }),
  });
}

function onReceiptPicked(e) {
  const file = e.target.files?.[0];
  if (!file) return;
  receiptFile.value = file;
  receiptPreview.value = URL.createObjectURL(file);
}

async function submitReceipt() {
  if (!receiptFile.value) {
    f7.dialog.alert(t("tontine.needReceipt"));
    return;
  }
  uploading.value = true;
  try {
    const receiptUrl = await uploadImageToR2(receiptFile.value);
    if (!receiptUrl) throw new Error(t("common.unableToUpload"));
    await store.submitReceipt(props.campaignId, props.contributionId, {
      receiptUrl,
      note: receiptNote.value || undefined,
    });
    f7.toast.show({
      text: t("tontine.receiptSubmitted"),
      position: "bottom",
      closeTimeout: 2000,
    });
    receiptPopupOpen.value = false;
    resetReceipt();
    await load();
  } catch (err) {
    f7.dialog.alert(
      err.response?.data?.message || err.message || t("tontine.receiptError"),
    );
  } finally {
    uploading.value = false;
  }
}

function openConfirmDialog() {
  const defAmount = contribution.value?.expectedAmount ?? "";
  f7.dialog.prompt(
    t("tontine.confirmPrompt", { amount: defAmount ? money(defAmount) : "" }),
    t("tontine.confirmContribution"),
    (amountRaw) => {
      const confirmedAmount = Number(
        String(amountRaw || "").replace(/[^0-9.]/g, ""),
      );
      if (!isFinite(confirmedAmount) || confirmedAmount < 0) {
        f7.dialog.alert(t("common.enterValidAmount"));
        return;
      }
      confirmContribution(confirmedAmount, null, null);
    },
    String(defAmount),
  );
}

async function confirmContribution(
  confirmedAmount,
  providerPaymentReference,
  note,
) {
  f7.preloader.show();
  try {
    await store.confirmContribution(props.campaignId, props.contributionId, {
      confirmedAmount: Number(confirmedAmount),
      confirmationSource: "TREASURER_CONFIRMED",
      providerPaymentReference: providerPaymentReference || undefined,
      note: note || undefined,
    });
    f7.toast.show({
      text: t("tontine.contributionConfirmed"),
      position: "bottom",
      closeTimeout: 2000,
    });
    await load();
  } catch (err) {
    f7.dialog.alert(
      err.response?.data?.message || err.message || t("tontine.confirmError"),
    );
  } finally {
    f7.preloader.hide();
  }
}

function resetReceipt() {
  receiptFile.value = null;
  receiptPreview.value = "";
  receiptNote.value = "";
  if (receiptInput.value) receiptInput.value.value = "";
}

const contributionStatusLabel = (s) =>
  ({
    DUE: t("tontine.contributionStatus.DUE"),
    PAYMENT_REQUESTED: t("tontine.contributionStatus.PAYMENT_REQUESTED"),
    PAYMENT_PENDING_CONFIRMATION: t(
      "tontine.contributionStatus.PAYMENT_PENDING_CONFIRMATION",
    ),
    RECEIPT_SUBMITTED: t("tontine.contributionStatus.RECEIPT_SUBMITTED"),
    CONFIRMED: t("tontine.contributionStatus.CONFIRMED"),
    OVERDUE: t("tontine.contributionStatus.OVERDUE"),
    NOT_DUE: t("tontine.contributionStatus.NOT_DUE"),
    EXCUSED: t("tontine.contributionStatus.EXCUSED"),
    WAIVED: t("tontine.contributionStatus.WAIVED"),
    REVERSED: t("tontine.contributionStatus.REVERSED"),
  }[s] ||
  s ||
  "—");

const contributionStatusClass = (s) => {
  if (s === "CONFIRMED") return "badge-green";
  if (s === "OVERDUE" || s === "REVERSED") return "badge-red";
  if (s === "EXCUSED" || s === "WAIVED") return "badge-gray";
  if (s === "RECEIPT_SUBMITTED" || s === "PAYMENT_PENDING_CONFIRMATION")
    return "badge-orange";
  return "badge-yellow";
};

function money(v, c) {
  if (v == null) return "—";
  return new Intl.NumberFormat(keycloak.locale || "fr", {
    style: "currency",
    currency: c || plan.value?.currency || "XOF",
    maximumFractionDigits: 0,
  }).format(v || 0);
}
function formatDate(v) {
  return v
    ? new Intl.DateTimeFormat(keycloak.locale || "fr", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(
        new Date(v.startsWith("20") && v.length === 10 ? v + "T00:00:00" : v),
      )
    : "—";
}
</script>

<style scoped>
.contribution-head {
  display: flex;
  align-items: center;
  gap: 12px;
}
.avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--f7-theme-color);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
}
.contribution-head-body h2 {
  margin: 0 0 6px;
  font-size: 20px;
}
.amount {
  font-size: 30px;
  font-weight: 700;
  margin: 14px 0 6px;
}
.badge-green {
  background: #00b894;
}
.badge-orange {
  background: #f0932b;
}
.badge-red {
  background: #ee5253;
}
.badge-gray {
  background: #9e9e9e;
}
.badge-yellow {
  background: #fdcb6e;
  color: #333;
}
.receipt-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: var(--f7-theme-color);
  margin-top: 8px;
}
.hint {
  font-size: 12px;
  color: var(--f7-on-surface-variant);
  margin-top: 8px;
}
.info-notice p {
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
}
.upload-zone {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  margin-bottom: 12px;
}
.upload-zone p {
  margin: 8px 0 0;
  font-size: 13px;
}
.receipt-preview {
  max-width: 100%;
  max-height: 260px;
  border-radius: 8px;
}
.receipt-name {
  font-size: 13px;
  font-weight: 600;
}
.hidden-input {
  display: none;
}
</style>
